import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  ListGroupItemText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import "../boxStyle.css";
import CopyToClipboard from "@/utils/CopyToClipboard";
import { useAppDispatch } from "@/services/hooks";
import { updateImageAltAction } from "@/services/ImageServices/ImageActions";
import { asCaught } from "@/services/caughtError";

/**
 * A Cloudinary image resource as the media library lists it (getImagesByFolder) or
 * an upload returns it. Every field is optional: a freshly uploaded row can lack
 * `format` / `uploaded_at` until a refresh, which the panel shows as "Require Refresh".
 */
export type MediaImage = {
  public_id?: string;
  secure_url?: string;
  url?: string;
  filename?: string;
  format?: string;
  uploaded_at?: string;
  context?: { alt?: string; custom?: { alt?: string } };
};

export type ImageInformationProps = {
  img?: MediaImage;
  modelCall?: boolean;
  closeModel?: () => void;
  onAltSaved?: (publicId: string, alt: string) => void;
};

/**
 * The alt stored on the resource (SCRUM-1593 writes it at upload). The Search API
 * behind getImagesByFolder returns `context` flat (`{ alt }`); the Upload API nests
 * it (`{ custom: { alt } }`) — read both so a resource from either source shows its
 * alt, and an image without `context` reads "" rather than undefined.
 */
export const altOf = (img: MediaImage | null | undefined): string =>
  typeof img?.context?.alt === "string"
    ? img.context.alt
    : typeof img?.context?.custom?.alt === "string"
      ? img.context.custom.alt
      : "";

const ImageInformation = ({
  img = {},
  modelCall = false,
  closeModel = () => {},
  onAltSaved = (_publicId: string, _alt: string) => {},
}: ImageInformationProps) => {
  const dispatch = useAppDispatch();
  const [modal, setModal] = useState(false);
  // SCRUM-1594: editable alt, pre-filled from the resource and re-synced when the
  // panel is handed another image.
  const [alt, setAlt] = useState(() => altOf(img));
  const [altStatus, setAltStatus] = useState<{ kind: "saved" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const storedAlt = altOf(img);
  // Kept (SCRUM-2178): the parent hands this panel another image, or the stored
  // alt changes after a save; the field follows the resource.
  useEffect(() => {
    setAlt(storedAlt);
  }, [storedAlt]);
  // Kept (SCRUM-2178): a saved/error message belongs to the image it was shown for.
  useEffect(() => {
    setAltStatus(null);
  }, [img?.public_id]);

  // open when parent asks
  useEffect(() => {
    if (modelCall) setModal(true);
  }, [modelCall]);

  const toggle = useCallback(() => {
    setModal(prev => {
      if (prev) closeModel(); // notify parent when closing
      return !prev;
    });
  }, [closeModel]);

  const safeId = (prefix: string, filename = "") =>
    `${prefix}${(filename || "").replace(/\s/g, "").substring(0, Math.ceil(filename.length / 2))}`;

  const truncate = (value: unknown, max = 46, suffix = " ...") =>
    typeof value === "string" ? value.substring(0, max) + (value.length > max ? suffix : "") : "";

  const fileName   = img?.filename || "";
  const secureUrl  = img?.secure_url;
  const url        = img?.url;
  const publicId   = img?.public_id;
  const format     = img?.format;
  const uploadedAt = img?.uploaded_at;

  const ids = {
    path: safeId("BTNPATH", fileName),
    secure: safeId("BTN", fileName),
    url: safeId("BTNURL", fileName),
    alt: safeId("ALT", fileName),
  };

  const saveAlt = async () => {
    if (!publicId || saving) return;
    setSaving(true);
    setAltStatus(null);
    try {
      await dispatch(updateImageAltAction(publicId, alt));
      onAltSaved(publicId, alt);
      setAltStatus({ kind: "saved", text: "Alt text saved" });
    } catch (e) {
      setAltStatus({ kind: "error", text: asCaught(e)?.message || "Failed to update alt text" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={modal} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        <span className="text-primary">{fileName}</span>
      </ModalHeader>

      <ModalBody>
        <div className="container text-center">
          <div className="box-img">{secureUrl ? <img src={secureUrl} alt={fileName} /> : null}</div>

          <ListGroup>
            <ListGroupItem>
              <ListGroupItemHeading>Name</ListGroupItemHeading>
              <ListGroupItemText><span>{fileName}</span></ListGroupItemText>
            </ListGroupItem>

            <ListGroupItem>
              <ListGroupItemHeading>Format</ListGroupItemHeading>
              <ListGroupItemText>
                <span>{format !== undefined ? format : "Require Refresh ..."}</span>
              </ListGroupItemText>
            </ListGroupItem>

            <ListGroupItem>
              <ListGroupItemHeading>Path</ListGroupItemHeading>
              {publicId !== undefined ? (
                <>
                  <ListGroupItemText><span>{truncate(publicId, 46, "")}</span></ListGroupItemText>
                  {/* CopyToClipboard renders its own button and shows "Copied!" itself. */}
                  <CopyToClipboard id={ids.path} text={publicId} />
                </>
              ) : (
                <ListGroupItemText><span>Require Refresh ...</span></ListGroupItemText>
              )}
            </ListGroupItem>

            <ListGroupItem>
              <ListGroupItemHeading>Secure URL</ListGroupItemHeading>
              {secureUrl !== undefined ? (
                <>
                  <ListGroupItemText><span>{truncate(secureUrl)}</span></ListGroupItemText>
                  <CopyToClipboard id={ids.secure} text={secureUrl} />
                </>
              ) : (
                <ListGroupItemText><span>Require Refresh ...</span></ListGroupItemText>
              )}
            </ListGroupItem>

            <ListGroupItem>
              <ListGroupItemHeading>URL</ListGroupItemHeading>
              {url !== undefined ? (
                <>
                  <ListGroupItemText><span>{truncate(url)}</span></ListGroupItemText>
                  <CopyToClipboard id={ids.url} text={url} />
                </>
              ) : (
                <ListGroupItemText><span>Require Refresh ...</span></ListGroupItemText>
              )}
            </ListGroupItem>

            <ListGroupItem>
              <ListGroupItemHeading>
                <Label for={ids.alt} className="mb-0">Alt text</Label>
              </ListGroupItemHeading>
              <div className="d-flex align-items-center mt-2">
                <Input
                  id={ids.alt}
                  type="text"
                  bsSize="sm"
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  disabled={publicId === undefined || saving}
                />
                <Button
                  color="primary"
                  size="sm"
                  className="ml-2 text-nowrap"
                  onClick={saveAlt}
                  disabled={publicId === undefined || saving || alt === storedAlt}
                >
                  Save alt
                </Button>
              </div>
              {altStatus ? (
                <ListGroupItemText
                  className={`mb-0 mt-1 ${altStatus.kind === "error" ? "text-danger" : "text-success"}`}
                  role="status"
                >
                  {altStatus.text}
                </ListGroupItemText>
              ) : null}
            </ListGroupItem>

            <ListGroupItem>
              <ListGroupItemHeading>Uploaded At</ListGroupItemHeading>
              <ListGroupItemText>
                {uploadedAt !== undefined ? (
                  <span>
                    {new Intl.DateTimeFormat("en-GB", {
                      year: "numeric",
                      month: "long",
                      day: "2-digit",
                    }).format(new Date(Date.parse(uploadedAt)))}
                  </span>
                ) : (
                  <span>Require Refresh ...</span>
                )}
              </ListGroupItemText>
            </ListGroupItem>
          </ListGroup>
        </div>
      </ModalBody>

      <ModalFooter />
    </Modal>
  );
};

export default ImageInformation;
